import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './RfiActions.css';
import printSvg from 'images/print.svg';
import mailSvg from 'images/mail.svg';
import { setShowEmailModal } from 'components/admin/projects/details/project-email-modal/store/actions';
import Button from 'components/shared/button/Button';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import {
  setRfiFormErrors,
  setRfiQuestionErrors,
  createRfi,
  updateRfi,
  respondRfi,
  respondRfiAddlAssignee,
  setRfiGcAcceleration,
  setRfiPdfViewer,
  deleteRfi,
  setSetShowRfiErrorMessage,
} from 'components/admin/projects/details/project-rfi-log/store/actions';
import { Enterprise } from 'domain/enterprise';
import { RfiStatusKeys, RfiStatusEnum } from 'domain/rfi-status-enum';
import { getRfiForm } from 'components/admin/projects/details/project-rfi-log/store/selectors';
import {
  getRfiQuestions,
  getRfiQuestionsDeleted,
  getRfiShowErrorMessage,
} from 'components/admin/projects/details/project-rfi-log/store/selectors';

class RfiActions extends Component {
  static propTypes = {
    currentUser: PropTypes.shape({}).isRequired,
    contribType: PropTypes.string.isRequired,
    project: PropTypes.shape({}).isRequired,
    currentRfi: PropTypes.shape({}),
    readOnly: PropTypes.bool,
    gcAcceleration: PropTypes.bool,
    handleClose: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = { showDeleteConfirmation: false };
  }

  pendingResponse = () => {
    const { currentUser, currentRfi } = this.props;
    return (
      !!currentRfi &&
      !!currentRfi.rfi_questions.find(
        (qus) =>
          qus.assignee_id === currentUser.id &&
          (!qus.response || qus.draft_response)
      )
    );
  };

  assigneeExists = () => {
    const { currentUser, questions } = this.props;
    return !!Object.keys(questions).find(
      (key) => questions[key].assignee[0] === currentUser.id
    );
  };

  addlAssigneeExists = () => {
    const { currentUser, currentRfi } = this.props;
    return (
      !!currentRfi &&
      !!currentRfi.rfi_questions
        .map((qus) => qus.rfi_addl_assignees)
        .flat()
        .find((addlAssignee) => addlAssignee.assignee_id === currentUser.id)
    );
  };

  addlAssigneeQuestions = (questions) => {
    const { currentUser } = this.props;
    return questions.filter(
      (question) =>
        !!question.rfi_addl_assignees.find(
          (addlAssignee) => addlAssignee.assignee_id === currentUser.id
        )
    );
  };

  assignedQuestions = () => {
    const { currentUser, currentRfi } = this.props;
    return currentRfi
      ? currentRfi.rfi_questions.filter(
          (question) =>
            question.assignee_id === currentUser.id ||
            !!question.rfi_addl_assignees.find(
              (addlAssignee) => addlAssignee.assignee_id === currentUser.id
            )
        )
      : [];
  };

  validateForm = (draft = false) => {
    const { contribType, form, dispatch } = this.props;
    const errors = {};
    const status = RfiStatusEnum.create(form.status);

    if (!Enterprise.primary(contribType)) {
      if (!form.csiCodeId) {
        errors.csiCodeId = true;
      }
      if (!draft) {
        if (!form.dueDate && !RfiStatusEnum.FOR_RECORD.equals(status)) {
          errors.dueDate = true;
        }
        if (!form.status) {
          errors.status = true;
        }
        if (!form.title) {
          errors.title = true;
        }
      }
      dispatch(setRfiFormErrors(errors));
    }
    return Object.keys(errors).length === 0;
  };

  validateQuestions = (draft = false) => {
    const {
      contribType,
      gcAcceleration,
      form,
      questions,
      dispatch,
    } = this.props;
    const status = RfiStatusEnum.create(form.status);
    const assignedQuestions =
      Enterprise.primary(contribType) && this.assignedQuestions();
    const oneAssignedQuestion =
      assignedQuestions && assignedQuestions.length === 1;
    let valid = true;
    Object.keys(questions).forEach((key) => {
      const question = questions[key];
      const assigned =
        oneAssignedQuestion && assignedQuestions[0].id === question.id;
      const errors = {};
      if (!question.action) {
        errors.action = true;
      }
      if (!question.question) {
        errors.question = true;
      }
      if (
        !draft &&
        RfiStatusEnum.ASSIGNED.equals(status) &&
        question.assignee.length === 0
      ) {
        errors.assignee = true;
      }
      if (
        !draft &&
        (assigned || gcAcceleration) &&
        question.id &&
        !question.response
      ) {
        errors.response = true;
      }
      dispatch(setRfiQuestionErrors(key, errors));
      valid = valid && Object.keys(errors).length === 0;
    });
    return valid;
  };

  validate = (draft = false) => {
    const validForm = this.validateForm(draft);
    const validQuestions = this.validateQuestions(draft);
    return validForm && validQuestions;
  };

  handleSaveDraft = () => {
    this.handleSubmit(true);
  };

  handleSubmit = (draft = false) => {
    if (!this.validate(draft)) {
      this.props.dispatch(setSetShowRfiErrorMessage(true));
      return;
    }
    const questions = Object.keys(this.props.questions)
      .map((key) => this.props.questions[key])
      .map((qus) => {
        return {
          ...qus,
          assigneeId: qus.assignee[0] || '',
          draftResponse: draft,
        };
      });
    const { form } = this.props;
    const rfi = {
      ...form,
      relCsiCodeIds: form.relCsiCodes.map((csiCode) => csiCode[0]),
      recipientIds: form.recipients.map((recpt) => recpt[0]),
      draft: draft || '',
      status: draft ? RfiStatusKeys.DRAFT : form.status,
      questions: questions,
      questionsDeleted: this.props.questionsDeleted,
    };
    const {
      currentUser,
      contribType,
      project,
      currentRfi,
      gcAcceleration,
      handleClose,
      dispatch,
    } = this.props;
    const promises = [];
    if (currentRfi) {
      if (Enterprise.primary(contribType)) {
        if (this.assigneeExists()) {
          rfi.questions = rfi.questions.filter(
            (qus) => qus.assignee[0] === currentUser.id
          );
          promises.push(dispatch(respondRfi(project.id, currentRfi.id, rfi)));
        }

        if (this.addlAssigneeExists()) {
          promises.push(
            dispatch(
              respondRfiAddlAssignee(
                currentUser.id,
                this.addlAssigneeQuestions(questions)
              )
            )
          );
        }
      } else {
        promises.push(dispatch(updateRfi(project.id, currentRfi.id, rfi)));

        if (gcAcceleration) {
          promises.push(dispatch(respondRfi(project.id, currentRfi.id, rfi)));
        }
      }
    } else {
      promises.push(dispatch(createRfi(project.id, rfi)));
    }

    Promise.all(promises).then((responses) => {
      if (!responses.find((response) => !response.ok)) {
        handleClose();
      }
    });
  };

  handleGcAcceleration = () => {
    this.props.dispatch(setRfiGcAcceleration(true));
  };

  handleViewPdf = () => {
    const { dispatch } = this.props;
    dispatch(setRfiPdfViewer(true));
  };

  handleShowEmailModal = () => {
    this.props.dispatch(setShowEmailModal(true));
  };

  handleDiscardDraft = () => {
    this.setState({ showDeleteConfirmation: true });
  };

  handleDeleteConfirmation = (confirmed) => {
    this.setState({ showDeleteConfirmation: false });
    confirmed && this.handleDelete();
  };

  handleDelete = () => {
    const { project, currentRfi, handleClose, dispatch } = this.props;
    dispatch(deleteRfi(project.id, currentRfi.id)).then((response) => {
      if (response.ok) {
        handleClose();
      }
    });
  };

  render() {
    const {
      contribType,
      currentRfi,
      readOnly,
      gcAcceleration,
      showErrorMessage,
      handleClose,
    } = this.props;
    const { showDeleteConfirmation } = this.state;
    const status = currentRfi && RfiStatusEnum.create(currentRfi.status);
    const primary = Enterprise.primary(contribType);
    const pendingResponse = this.pendingResponse();
    const assigneeExists = this.assigneeExists();
    const addlAssigneeExists = this.addlAssigneeExists();
    const spackleOrGc =
      Enterprise.spackle(contribType) ||
      Enterprise.generalContractor(contribType);
    return (
      <div className="rfi-actions">
        {showErrorMessage && (
          <h4 className="error-message">
            There are missing fields, please review.
          </h4>
        )}
        <div className="buttons-container">
          {currentRfi && (
            <React.Fragment>
              <Button
                className="button-white"
                icon={mailSvg}
                label="Email"
                onClick={this.handleShowEmailModal}
              />
              {currentRfi.pdf_document && (
                <Button
                  className="button-white"
                  icon={printSvg}
                  label="View PDF"
                  onClick={this.handleViewPdf}
                />
              )}
            </React.Fragment>
          )}
          {!readOnly &&
            (!currentRfi ||
              RfiStatusEnum.DRAFT.equals(status) ||
              pendingResponse) && (
              <Button
                className="button-white button-save-draft"
                label="Save Draft and Close"
                onClick={this.handleSaveDraft}
              />
            )}
          {!readOnly && !gcAcceleration && currentRfi && spackleOrGc && (
            <Button
              className="button-white"
              label="GC Acceleration"
              onClick={this.handleGcAcceleration}
            />
          )}
          {!readOnly && (!primary || assigneeExists || addlAssigneeExists) && (
            <Button
              className="button-orange"
              label="Submit"
              onClick={() => this.handleSubmit()}
            />
          )}
          {currentRfi &&
            (RfiStatusEnum.DRAFT.equals(status) ||
              RfiStatusEnum.PENDING.equals(status)) && (
              <Button
                className="button-white button-delete"
                label="Discard"
                onClick={this.handleDiscardDraft}
              />
            )}
          <Button
            className="button-gray"
            label={readOnly ? 'Close' : 'Cancel'}
            onClick={handleClose}
          />
        </div>
        {showDeleteConfirmation && (
          <ConfirmationModal
            show={showDeleteConfirmation}
            message="Are you sure you want to discard this RFI?"
            onHide={this.handleDeleteConfirmation}
          />
        )}
      </div>
    );
  }
}

export default connect((state) => {
  return {
    form: getRfiForm(state),
    questions: getRfiQuestions(state),
    questionsDeleted: getRfiQuestionsDeleted(state),
    showErrorMessage: getRfiShowErrorMessage(state),
  };
})(RfiActions);



// WEBPACK FOOTER //
// ./src/components/admin/projects/details/project-rfi-log/RfiActions.js